import axios, { AxiosInstance } from 'axios';
import { API } from '@/lib/Client';
import { InvestorProfile, SAMPLE_PORTFOLIO_DATA } from '@/pages/your-type/constants';

const SUPABASE_URL = process.env.NEXT_PUBLIC_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

const createSupabaseClient = (): AxiosInstance => {
    return axios.create({
        baseURL: `${SUPABASE_URL}/rest/v1`,
        headers: {
            'apikey': SUPABASE_ANON_KEY || '',
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
            'Prefer': 'return=representation',
        },
    });
};

const supabaseClient = createSupabaseClient();

const GENERATED_COURSES_TABLE = 'GeneratedCourses';
const STOCK_HOOKS_TABLE = 'StockHooks';
const PERPLEXITY_PROMPTS_TABLE = 'PerplexityPrompts';
const GLOBAL_LESSON_INSTRUCTIONS_TABLE = 'GlobalLessonInstructions';
const PERSONALITY_ANALYSIS_TABLE = 'PersonalityAnalysis';

export interface StoredGeneratedCoursePayload {
    outline: any;
    generatedLessons?: { [key: string]: any };
    lessonCompletion?: {
        [key: string]: {
            completed: boolean;
            completedAt?: string;
            attempts?: number;
        };
    };
    metadata?: {
        stockName?: string;
        ticker?: string;
        goal?: string;
        level?: string;
        selectedTopics?: { [key: string]: string[] };
        topicKeyMapping?: { [chapterTitle: string]: string };
        generatedAt?: string;
        [key: string]: any;
    };
}

export interface GeneratedCourseRecord {
    id: string;
    userid?: string;
    created_at: string;
    course_title: string;
    course_payload: StoredGeneratedCoursePayload;
    branch_tag?: string | null;
    notes?: string | null;
    ticker?: string | null;
}

export interface StockHookRecord {
    token: string;
    hook_text: string;
    hook_texts?: string[]; // Array of hooks for slider
    generated_at: string;
}

export interface PersonalityAnalysisRecord {
    ticker: string;
    stock_name: string;
    analysis_data: {
        title: string;
        summary: string;
        personalityTraits: string[];
        blindSpots: string;
        mbtiType: string;
        investorType: string;
    };
    generated_at: string;
}

export async function insertGeneratedCourseSnapshot(params: {
    courseTitle: string;
    coursePayload: StoredGeneratedCoursePayload;
    notes?: string;
    ticker?: string;
    userId?: string;
}): Promise<GeneratedCourseRecord | null> {
    try {
        const id = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : Array.from(Array(16), () => Math.floor(Math.random() * 16).toString(16)).join(''); // Simple fallback if crypto.randomUUID is not available
        const response = await supabaseClient.post(
            `/${GENERATED_COURSES_TABLE}?select=*`,
            {
                id,
                userid: params.userId,
                course_title: params.courseTitle,
                course_payload: params.coursePayload,
                notes: params.notes ?? null,
                ticker: params.ticker ?? null,
            },
            {
                headers: {
                    'Prefer': 'return=representation',
                },
            }
        );

        const data = response.data as GeneratedCourseRecord | GeneratedCourseRecord[] | null;
        if (data && Array.isArray(data) && data.length > 0) {
            return data[0] as GeneratedCourseRecord;
        }

        return (data as GeneratedCourseRecord) || null;
    } catch (error: any) {
        console.error('Error inserting generated course snapshot:', error);
        const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
        throw new Error(`Failed to save generated course snapshot: ${errorMessage}`);
    }
}

export async function updateGeneratedCourseSnapshot(params: {
    id: string;
    coursePayload: StoredGeneratedCoursePayload;
    notes?: string | null;
}): Promise<void> {
    try {
        await supabaseClient.patch(
            `/${GENERATED_COURSES_TABLE}?id=eq.${params.id}`,
            {
                course_payload: params.coursePayload,
                notes: params.notes ?? null,
            }
        );
    } catch (error: any) {
        console.error('Error updating generated course snapshot:', error);
        const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
        throw new Error(`Failed to update generated course snapshot: ${errorMessage}`);
    }
}

export async function fetchMostRecentCourseByTicker(ticker: string, userId?: string): Promise<GeneratedCourseRecord | null> {
    try {
        let url = `/${GENERATED_COURSES_TABLE}?ticker=eq.${ticker}&select=*&order=created_at.desc&limit=1`;
        if (userId) {
            url += `&userid=eq.${userId}`;
        }
        const response = await supabaseClient.get(url);

        const data = response.data as GeneratedCourseRecord | GeneratedCourseRecord[] | null;
        if (data && Array.isArray(data) && data.length > 0) {
            return data[0] as GeneratedCourseRecord;
        }
        if (data && !Array.isArray(data)) {
            return data as GeneratedCourseRecord;
        }

        return null;
    } catch (error: any) {
        console.error('Error fetching course by ticker:', error);
        return null;
    }
}

export async function fetchGeneratedCourseSnapshots(userId?: string, limit = 50): Promise<GeneratedCourseRecord[]> {
    try {
        let url = `/${GENERATED_COURSES_TABLE}?select=*&order=created_at.desc&limit=${limit}`;
        if (userId) {
            url += `&userid=eq.${userId}`;
        }
        const response = await supabaseClient.get(url);

        return (response.data ?? []) as GeneratedCourseRecord[];
    } catch (error: any) {
        console.error('Error fetching generated course snapshots:', error);
        const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
        throw new Error(`Failed to fetch generated course snapshots: ${errorMessage}`);
    }
}

export async function fetchGeneratedCourseSnapshotById(id: string, userId?: string): Promise<GeneratedCourseRecord | null> {
    try {
        let url = `/${GENERATED_COURSES_TABLE}?select=*&id=eq.${id}`;
        if (userId) {
            url += `&userid=eq.${userId}`;
        }
        const response = await supabaseClient.get(
            url,
            {
                headers: {
                    'Prefer': 'return=representation',
                },
            }
        );

        const data = response.data as GeneratedCourseRecord | GeneratedCourseRecord[] | null;
        if (data && Array.isArray(data) && data.length > 0) {
            return data[0] as GeneratedCourseRecord;
        }

        return (data as GeneratedCourseRecord) || null;
    } catch (error: any) {
        console.error('Error fetching generated course snapshot by ID:', error);
        if (error?.response?.status === 404) {
            return null;
        }
        return null;
    }
}

export async function fetchStockHook(token: string): Promise<StockHookRecord | null> {
    try {
        try {
            const response = await supabaseClient.get(
                `/${STOCK_HOOKS_TABLE}?select=token,hook_text,hook_texts,generated_at&token=eq.${token}`,
                {
                    headers: {
                        'Prefer': 'return=representation',
                    },
                }
            );

            const data = response.data as StockHookRecord | StockHookRecord[] | null;
            if (data && Array.isArray(data) && data.length > 0) {
                return data[0] as StockHookRecord;
            }

            return (data as StockHookRecord) || null;
        } catch (error: any) {
            if (error?.response?.status === 400 || error?.response?.data?.message?.includes('hook_texts')) {
                const fallbackResponse = await supabaseClient.get(
                    `/${STOCK_HOOKS_TABLE}?select=token,hook_text,generated_at&token=eq.${token}`,
                    {
                        headers: {
                            'Prefer': 'return=representation',
                        },
                    }
                );

                const fallbackData = fallbackResponse.data as StockHookRecord | StockHookRecord[] | null;
                if (fallbackData && Array.isArray(fallbackData) && fallbackData.length > 0) {
                    return fallbackData[0] as StockHookRecord;
                }

                return (fallbackData as StockHookRecord) || null;
            }
            throw error;
        }
    } catch (error: any) {
        console.error('Error fetching stock hook:', error);
        const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
        throw new Error(`Failed to fetch stock hook: ${errorMessage}`);
    }
}

export async function upsertStockHook(token: string, hookText: string, hookTexts?: string[]): Promise<StockHookRecord> {
    try {
        try {
            const response = await supabaseClient.post(
                `/${STOCK_HOOKS_TABLE}?select=token,hook_text,hook_texts,generated_at`,
                {
                    token,
                    hook_text: hookText,
                    hook_texts: hookTexts,
                },
                {
                    headers: {
                        'Prefer': 'resolution=merge-duplicates,return=representation',
                    },
                }
            );

            const data = response.data as StockHookRecord | StockHookRecord[] | null;
            if (data && Array.isArray(data) && data.length > 0) {
                return data[0] as StockHookRecord;
            }

            return data as StockHookRecord;
        } catch (error: any) {
            if (error?.response?.status === 400 || error?.response?.data?.message?.includes('hook_texts')) {
                console.warn('hook_texts column does not exist, saving without it. Please add the column to the database.');

                const fallbackResponse = await supabaseClient.post(
                    `/${STOCK_HOOKS_TABLE}?select=token,hook_text,generated_at`,
                    {
                        token,
                        hook_text: hookText,
                    },
                    {
                        headers: {
                            'Prefer': 'resolution=merge-duplicates,return=representation',
                        },
                    }
                );

                const fallbackData = fallbackResponse.data as StockHookRecord | StockHookRecord[] | null;
                if (fallbackData && Array.isArray(fallbackData) && fallbackData.length > 0) {
                    return { ...fallbackData[0], hook_texts: hookTexts } as StockHookRecord;
                }

                return { ...(fallbackData as StockHookRecord), hook_texts: hookTexts } as StockHookRecord;
            }
            throw error;
        }
    } catch (error: any) {
        console.error('Error in upsertStockHook:', error);
        const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
        throw new Error(`Failed to save stock hook: ${errorMessage}`);
    }
}

export async function fetchPerplexityPrompt(topicKey: string): Promise<{
    prompt: string;
    searchDomainFilter?: string[];
    searchRecencyFilter?: string;
}> {
    try {
        const response = await supabaseClient.get(
            `/${PERPLEXITY_PROMPTS_TABLE}?select=prompt,search_domain_filter,search_recency_filter&topic_key=eq.${topicKey}&is_active=eq.true`,
            {
                headers: {
                    'Prefer': 'return=representation',
                },
            }
        );

        const responseData = response.data as any;
        let data: any;
        if (responseData && Array.isArray(responseData) && responseData.length > 0) {
            data = responseData[0];
        } else {
            data = responseData;
        }

        if (!data || !data.prompt) {
            throw new Error(`No active Perplexity prompt found for topic: ${topicKey}`);
        }

        return {
            prompt: data.prompt,
            searchDomainFilter: data.search_domain_filter || undefined,
            searchRecencyFilter: data.search_recency_filter || undefined,
        };
    } catch (error: any) {
        console.error(`Error fetching Perplexity prompt for ${topicKey}:`, error);
        const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
        throw new Error(`Failed to fetch Perplexity prompt: ${errorMessage}`);
    }
}

export async function fetchChapterGenerationPrompt(): Promise<string> {
    try {
        const response = await supabaseClient.get(
            `/${GLOBAL_LESSON_INSTRUCTIONS_TABLE}?select=instructions&is_active=eq.true`,
            {
                headers: {
                    'Prefer': 'return=representation',
                },
            }
        );

        const responseData = response.data as any;
        let data: any;
        if (responseData && Array.isArray(responseData) && responseData.length > 0) {
            data = responseData[0];
        } else {
            data = responseData;
        }

        if (!data || !data.instructions) {
            throw new Error('No active chapter generation prompt found in database');
        }

        return data.instructions;
    } catch (error: any) {
        console.error('Error fetching chapter generation prompt:', error);
        const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
        throw new Error(`Failed to fetch chapter generation prompt: ${errorMessage}`);
    }
}

export async function fetchPersonalityAnalysis(ticker: string): Promise<{
    title: string;
    summary: string;
    personalityTraits: string[];
    blindSpots: string;
    mbtiType: string;
    investorType: string;
} | null> {
    try {
        const response = await supabaseClient.get(
            `/${PERSONALITY_ANALYSIS_TABLE}?select=ticker,stock_name,analysis_data,generated_at&ticker=eq.${ticker}`,
            {
                headers: {
                    'Prefer': 'return=representation',
                },
            }
        );

        const data = response.data as PersonalityAnalysisRecord | PersonalityAnalysisRecord[] | null;
        let record: PersonalityAnalysisRecord | null = null;

        if (data && Array.isArray(data) && data.length > 0) {
            record = data[0];
        } else if (data && !Array.isArray(data)) {
            record = data;
        }

        if (!record || !record.analysis_data) {
            return null;
        }

        return record.analysis_data;
    } catch (error: any) {
        console.error('Error fetching personality analysis:', error);
        return null;
    }
}

export async function savePersonalityAnalysis(params: {
    ticker: string;
    stockName: string;
    title: string;
    summary: string;
    personalityTraits: string[];
    blindSpots: string;
    mbtiType: string;
    investorType: string;
}): Promise<void> {
    try {
        const analysisData = {
            title: params.title,
            summary: params.summary,
            personalityTraits: params.personalityTraits,
            blindSpots: params.blindSpots,
            mbtiType: params.mbtiType,
            investorType: params.investorType,
        };

        await supabaseClient.post(
            `/${PERSONALITY_ANALYSIS_TABLE}?select=ticker`,
            {
                ticker: params.ticker,
                stock_name: params.stockName,
                analysis_data: analysisData,
            },
            {
                headers: {
                    'Prefer': 'resolution=merge-duplicates,return=representation',
                },
            }
        );
    } catch (error: any) {
        console.error('Error saving personality analysis:', error);
        const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
        console.warn(`Failed to save personality analysis: ${errorMessage}`);
    }
}

// Edge Function for Personality Quiz (lulustudio_project_tracking)
// TODO: Change this for it to use supabase client call
export async function fetchPersonalityRecommendations(params: {
  answers?: Record<number, 'A' | 'B' | 'C' | 'D' | 'E'>;
  personalityType?: string;
  riskTolerance?: string;
  timeHorizon?: string;
  fundId?: string;
}) {
  const edgeFunctionUrl = SUPABASE_URL + '/functions/v1/get-personality-recommendations';
  const response = await fetch(edgeFunctionUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'apikey': SUPABASE_ANON_KEY || '',
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify(params),
  });
  
  if (!response.ok) {
    throw new Error(`Failed to fetch personality recommendations: ${response.statusText}`);
  }
  
  return response.json();
}

/**
 * Fetches comprehensive fund details from the get-fund-details edge function.
 * Returns portfolio stats, live holdings, top buys/sells, and return bars (1Y + 5Y).
 */
export async function fetchFundDetails(fundId: string) {
  const edgeFunctionUrl = SUPABASE_URL + '/functions/v1/get-fund-details';
  const response = await fetch(edgeFunctionUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'apikey': SUPABASE_ANON_KEY || '',
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify({ fundId }),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch fund details: ${response.statusText}`);
  }

  return response.json() as Promise<{
    details: any;
    holdings: { holdings: any[] } | null;
    topBuysSells: { buys: any[]; sells: any[]; reporting_year: number; reporting_quarter: number } | null;
    returnBars1Y: { bars: any[] } | null;
    returnBars5Y: { bars: any[] } | null;
  }>;
}

/**
 * Fetches an AI-generated custom portfolio based on the user's personality profile and quiz answers.
 * Calls the /ainvest endpoint on the Bloom API.
 */
export async function fetchAiCustomPortfolio(profile: InvestorProfile, answers: Record<number, 'A' | 'B' | 'C' | 'D' | 'E'>) {
  try {
    const riskLevel = profile.riskTolerance.toLowerCase();
    const timeHorizon = profile.timeHorizon;
    const traits = profile.traits.join(', ');
    
    const question = `
      Build a custom 15-stock index/portfolio for a user with the following profile:
      - Personality Type: ${profile.name} (${profile.id})
      - Key Traits: ${traits}
      - Risk Tolerance: ${riskLevel}
      - Time Horizon: ${timeHorizon}
      
      The user's core investment philosophy is: "${profile.description}"
      
      Please build a portfolio that truly reflects this personality. 
      Include at least 15 diverse stocks. For each stock, explain how it fits this specific personality type.
      Ensure the total allocation is exactly 100%.
    `;

    console.log('[fetchAiCustomPortfolio] Making request with question:', question);

    // Use a longer timeout for AI generation (60 seconds instead of default 10)
    const response = await API.post('/general-chatbot/ainvest', {
      question,
      messages: []
    }, {
      timeout: 60000, // 60 seconds
    });

    // The backend returns the choice object from OpenAI
    const aiResponse = response.data;
    const content = aiResponse?.message?.content;
    
    console.log('[fetchAiCustomPortfolio] Raw AI response:', aiResponse);
    console.log('[fetchAiCustomPortfolio] Content:', content);
    
    if (!content) {
      console.error('[fetchAiCustomPortfolio] No content in response');
      // If content is missing/empty (which happens when quota is exhausted), throw to trigger fallback
      throw new Error('No content returned from AI');
    }

    try {
      const parsed = JSON.parse(content);
      console.log('[fetchAiCustomPortfolio] Successfully parsed JSON:', parsed);
      return parsed;
    } catch (e) {
      console.log('[fetchAiCustomPortfolio] Failed to parse as direct JSON, trying markdown extraction');
      // In case the AI returns markdown-wrapped JSON
      const jsonMatch = content.match(/```json\n([\s\S]*?)\n```/) || content.match(/{[\s\S]*}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[1] || jsonMatch[0]);
        console.log('[fetchAiCustomPortfolio] Successfully parsed from markdown:', parsed);
        return parsed;
      }
      console.error('[fetchAiCustomPortfolio] Complete parsing failure. Original content:', content);
      throw e;
    }
  } catch (error: any) {
    console.error('[fetchAiCustomPortfolio] Error details:', {
      message: error?.message,
      response: error?.response?.data,
      status: error?.response?.status,
      code: error?.code,
    });
    
    // Fallback for SyntaxError (empty body/JSON parse error) or other API failures
    // This handles the "quota exhausted" case where backend returns undefined/empty body
    if (
      error instanceof SyntaxError || 
      error?.message?.includes('JSON') || 
      error?.message?.includes('No content') ||
      !error?.response // Network errors or empty responses often lack response object
    ) {
      console.warn('[fetchAiCustomPortfolio] Falling back to sample data due to API error/quota exhaustion');
      return SAMPLE_PORTFOLIO_DATA;
    }
    
    throw error;
  }
}

// ─── Bloomie Agent ───────────────────────────────────────────────────────────

export interface BloomieProfile {
  id?: string;
  firebase_uid: string;
  phone_number?: string | null;
  telegram_chat_id?: string | null;
  email?: string | null;
  interests?: string[];
  signal_style?: 'chill' | 'balanced' | 'alpha';
  watchlist?: string[];
  selected_categories?: string[];
  intensity?: number;
  market_hours_only?: boolean;
  tone?: 'neutral' | 'friendly' | 'super short' | 'detailed';
  messenger?: 'telegram' | 'whatsapp' | 'sms';
  min_price_move_pct?: number;
  cooldown_minutes?: number;
  max_alerts_per_hour?: number;
  repeat_tolerance?: 'strict' | 'balanced' | 'relaxed';
  last_signal_hash?: string | null;
  last_notified_at?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface BloomieSignal {
  id?: string;
  firebase_uid: string;
  ticker?: string | null;
  messenger?: string | null;
  message_sent?: string | null;
  action_signal?: string | null;
  action_url?: string | null;
  news_url?: string | null;
  sent_at?: string;
  status?: 'sent' | 'failed';
  hook?: string | null;
  tldr?: string | null;
  so_what?: string | null;
  move_reason?: string | null;
}

export async function fetchBloomieProfile(firebaseUid: string): Promise<BloomieProfile | null> {
  try {
    const response = await supabaseClient.get('/BloomieProfiles', {
      params: { firebase_uid: `eq.${firebaseUid}`, limit: 1 },
    });
    const rows = response.data as BloomieProfile[];
    return rows?.[0] ?? null;
  } catch (error) {
    console.error('[fetchBloomieProfile] Error:', error);
    return null;
  }
}

export async function upsertBloomieProfile(profile: BloomieProfile): Promise<BloomieProfile | null> {
  try {
    const response = await supabaseClient.post('/BloomieProfiles', {
      ...profile,
      updated_at: new Date().toISOString(),
    }, {
      headers: { Prefer: 'resolution=merge-duplicates,return=representation' },
    });
    const rows = response.data as BloomieProfile[];
    return rows?.[0] ?? null;
  } catch (error) {
    console.error('[upsertBloomieProfile] Error:', error);
    throw error;
  }
}

export async function deleteBloomieProfile(firebaseUid: string): Promise<boolean> {
  try {
    await Promise.all([
      supabaseClient.delete('/BloomieProfiles', {
        params: { firebase_uid: `eq.${firebaseUid}` },
      }),
      supabaseClient.delete('/BloomieSignals', {
        params: { firebase_uid: `eq.${firebaseUid}` },
      }),
    ]);
    return true;
  } catch (error) {
    console.error('[deleteBloomieProfile] Error:', error);
    return false;
  }
}

export async function fetchBloomieSignals(firebaseUid: string, limit = 10): Promise<BloomieSignal[]> {
  try {
    const response = await supabaseClient.get('/BloomieSignals', {
      params: {
        firebase_uid: `eq.${firebaseUid}`,
        order: 'sent_at.desc',
        limit,
      },
    });
    return (response.data as BloomieSignal[]) ?? [];
  } catch (error) {
    console.error('[fetchBloomieSignals] Error:', error);
    return [];
  }
}

export async function insertBloomieSignal(signal: BloomieSignal): Promise<void> {
  try {
    await supabaseClient.post('/BloomieSignals', signal);
  } catch (error) {
    console.error('[insertBloomieSignal] Error:', error);
  }
}

// ─── Bloomie Monitor ────────────────────────────────────────────────────────

export interface BloomieNewsItem {
  id: string;
  news_url: string;
  title: string;
  text_content?: string;
  source_name: string;
  published_at: string;
  image_url?: string;
  tickers: string[];
  topics: string[];
  sentiment?: string;
  relevance_score: number;
  category?: string;
  processed: boolean;
  hook?: string;
  tldr?: string;
  so_what?: string;
  move_reason?: string;
  action_signal?: string;
  action_reasoning?: string;
  fetch_batch_id?: string;
  processing_time_ms?: number;
  signals_sent_count?: number;
  news_batch_id?: string;
  tickers_inferred?: boolean;
  source_tier?: number;
  ticker_fetch_source?: string;
  created_at: string;
}

export interface BloomieSignalDelivery {
  id: string;
  news_item_id?: string;
  news_batch_id?: string;
  signal_id?: string;
  firebase_uid: string;
  matched_tickers: string[];
  matched_categories: string[];
  relevance_score?: number;
  match_rank?: number;
  delivery_status: 'matched' | 'sent' | 'failed' | 'skipped';
  skipped_reason?: string;
  created_at: string;
}

export interface BloomiePipelineRun {
  id: string;
  started_at: string;
  completed_at?: string;
  status: 'running' | 'completed' | 'failed';
  articles_fetched: number;
  articles_new: number;
  articles_processed: number;
  signals_matched: number;
  signals_sent: number;
  signals_failed: number;
  error_message?: string;
  trigger_source: 'cron' | 'manual' | 'demo';
  batches_created?: number;
  articles_batched?: number;
  tickers_queried?: string[];
  ticker_demand_count?: number;
}

export interface BloomieTickerDemand {
  ticker: string;
  demand_count: number;
  source: string;
  last_fetched_at: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface SourceBreakdown {
  source_name: string;
  article_count: number;
  avg_relevance: number;
  tier: number;
}

export interface BloomieNewsBatch {
  id: string;
  representative_hook?: string;
  representative_tldr?: string;
  representative_so_what?: string;
  representative_move_reason?: string;
  representative_action_signal?: string;
  representative_sentiment?: string;
  tickers: string[];
  categories: string[];
  relevance_score: number;
  article_count: number;
  sources: string[];
  fetch_batch_id?: string;
  pipeline_run_id?: string;
  earliest_published_at?: string;
  latest_published_at?: string;
  created_at: string;
  updated_at?: string;
}

export interface FlowBatch extends BloomieNewsBatch {
  articles: BloomieNewsItem[];
  deliveries: BloomieSignalDelivery[];
}

export interface MonitorStats {
  total_news: number;
  processed_news: number;
  total_signals: number;
  active_users: number;
  recent_pipeline_runs: BloomiePipelineRun[];
}

export interface NewsItemDetail extends BloomieNewsItem {
  deliveries: BloomieSignalDelivery[];
}

export interface NewsFilterParams {
  processed?: boolean;
  ticker?: string;
  category?: string;
  days?: number;
  limit?: number;
  offset?: number;
  sourceTier?: number;
}

export async function fetchMonitorStats(): Promise<MonitorStats> {
  try {
    const [newsRes, processedRes, signalsRes, usersRes, runsRes] = await Promise.all([
      supabaseClient.get('/BloomieNewsItems', { params: { select: 'id', limit: 1 }, headers: { Prefer: 'count=exact' } }),
      supabaseClient.get('/BloomieNewsItems', { params: { select: 'id', processed: 'eq.true', limit: 1 }, headers: { Prefer: 'count=exact' } }),
      supabaseClient.get('/BloomieSignals', { params: { select: 'id', limit: 1 }, headers: { Prefer: 'count=exact' } }),
      supabaseClient.get('/BloomieProfiles', { params: { select: 'id', limit: 1 }, headers: { Prefer: 'count=exact' } }),
      supabaseClient.get('/BloomiePipelineRuns', { params: { order: 'started_at.desc', limit: 10 } }),
    ]);

    const parseCount = (res: any) => {
      const range = res.headers?.['content-range'] || '';
      const match = range.match(/\/(\d+)/);
      return match ? parseInt(match[1], 10) : (res.data?.length ?? 0);
    };

    return {
      total_news: parseCount(newsRes),
      processed_news: parseCount(processedRes),
      total_signals: parseCount(signalsRes),
      active_users: parseCount(usersRes),
      recent_pipeline_runs: (runsRes.data || []) as BloomiePipelineRun[],
    };
  } catch (error) {
    console.error('[fetchMonitorStats] Error:', error);
    return { total_news: 0, processed_news: 0, total_signals: 0, active_users: 0, recent_pipeline_runs: [] };
  }
}

export async function fetchNewsItems(params: NewsFilterParams = {}): Promise<{ items: BloomieNewsItem[]; totalCount: number }> {
  try {
    const queryParams: Record<string, string> = {
      order: 'created_at.desc',
      limit: String(params.limit || 50),
      offset: String(params.offset || 0),
    };
    if (params.processed !== undefined) queryParams.processed = `eq.${params.processed}`;
    if (params.ticker) queryParams.tickers = `cs.{${params.ticker.toUpperCase()}}`;
    if (params.category) queryParams.category = `eq.${params.category}`;
    if (params.days) {
      const since = new Date(Date.now() - params.days * 86400000).toISOString();
      queryParams.created_at = `gte.${since}`;
    }
    if (params.sourceTier) queryParams.source_tier = `gte.${params.sourceTier}`;

    const response = await supabaseClient.get('/BloomieNewsItems', {
      params: queryParams,
      headers: { Prefer: 'count=exact' },
    });
    const range = response.headers?.['content-range'] || '';
    const match = range.match(/\/(\d+)/);
    const totalCount = match ? parseInt(match[1], 10) : (response.data?.length ?? 0);
    return { items: (response.data || []) as BloomieNewsItem[], totalCount };
  } catch (error) {
    console.error('[fetchNewsItems] Error:', error);
    return { items: [], totalCount: 0 };
  }
}

export async function fetchNewsItemDetail(id: string): Promise<NewsItemDetail | null> {
  try {
    const [newsRes, deliveriesRes] = await Promise.all([
      supabaseClient.get('/BloomieNewsItems', { params: { id: `eq.${id}`, limit: 1 } }),
      supabaseClient.get('/BloomieSignalDeliveries', { params: { news_item_id: `eq.${id}`, order: 'created_at.desc' } }),
    ]);
    const item = (newsRes.data as BloomieNewsItem[])?.[0];
    if (!item) return null;
    return { ...item, deliveries: (deliveriesRes.data || []) as BloomieSignalDelivery[] };
  } catch (error) {
    console.error('[fetchNewsItemDetail] Error:', error);
    return null;
  }
}

export async function fetchAllSignals(limit = 50): Promise<BloomieSignal[]> {
  try {
    const response = await supabaseClient.get('/BloomieSignals', {
      params: { order: 'sent_at.desc', limit },
    });
    return (response.data || []) as BloomieSignal[];
  } catch (error) {
    console.error('[fetchAllSignals] Error:', error);
    return [];
  }
}

export async function fetchAllProfiles(): Promise<BloomieProfile[]> {
  try {
    const response = await supabaseClient.get('/BloomieProfiles', {
      params: { order: 'created_at.desc' },
    });
    return (response.data || []) as BloomieProfile[];
  } catch (error) {
    console.error('[fetchAllProfiles] Error:', error);
    return [];
  }
}

export async function fetchPipelineRuns(limit = 20): Promise<BloomiePipelineRun[]> {
  try {
    const response = await supabaseClient.get('/BloomiePipelineRuns', {
      params: { order: 'started_at.desc', limit },
    });
    return (response.data || []) as BloomiePipelineRun[];
  } catch (error) {
    console.error('[fetchPipelineRuns] Error:', error);
    return [];
  }
}

export async function fetchSignalDeliveries(newsItemId: string): Promise<BloomieSignalDelivery[]> {
  try {
    const response = await supabaseClient.get('/BloomieSignalDeliveries', {
      params: { news_item_id: `eq.${newsItemId}`, order: 'created_at.desc' },
    });
    return (response.data || []) as BloomieSignalDelivery[];
  } catch (error) {
    console.error('[fetchSignalDeliveries] Error:', error);
    return [];
  }
}

export async function fetchNewsBatches(days = 7, limit = 50): Promise<BloomieNewsBatch[]> {
  try {
    const since = new Date(Date.now() - days * 86400000).toISOString();
    const response = await supabaseClient.get('/BloomieNewsBatches', {
      params: {
        order: 'updated_at.desc',
        limit,
        created_at: `gte.${since}`,
      },
    });
    return (response.data || []) as BloomieNewsBatch[];
  } catch (error) {
    console.error('[fetchNewsBatches] Error:', error);
    return [];
  }
}

export interface FlowData {
  batches: FlowBatch[];
  /** Articles still being processed (not yet batched) */
  processingItems: BloomieNewsItem[];
}

export async function fetchFlowData(days = 1): Promise<FlowData> {
  try {
    const since = new Date(Date.now() - days * 86400000).toISOString();

    const [batchesRes, batchedArticlesRes, deliveriesRes, unbatchedRes] = await Promise.all([
      supabaseClient.get('/BloomieNewsBatches', {
        params: { order: 'updated_at.desc', limit: 50, created_at: `gte.${since}` },
      }),
      supabaseClient.get('/BloomieNewsItems', {
        params: { order: 'created_at.desc', limit: 500, created_at: `gte.${since}`, news_batch_id: 'not.is.null' },
      }),
      supabaseClient.get('/BloomieSignalDeliveries', {
        params: { order: 'created_at.desc', limit: 500, created_at: `gte.${since}`, news_batch_id: 'not.is.null' },
      }),
      // Fetch unbatched items (still processing or processed but not yet clustered)
      supabaseClient.get('/BloomieNewsItems', {
        params: { order: 'created_at.desc', limit: 100, created_at: `gte.${since}`, news_batch_id: 'is.null' },
      }),
    ]);

    const batches = (batchesRes.data || []) as BloomieNewsBatch[];
    const articles = (batchedArticlesRes.data || []) as BloomieNewsItem[];
    const deliveries = (deliveriesRes.data || []) as BloomieSignalDelivery[];
    const processingItems = (unbatchedRes.data || []) as BloomieNewsItem[];

    const articlesByBatch = new Map<string, BloomieNewsItem[]>();
    for (const a of articles) {
      if (!a.news_batch_id) continue;
      const list = articlesByBatch.get(a.news_batch_id) || [];
      list.push(a);
      articlesByBatch.set(a.news_batch_id, list);
    }

    const deliveriesByBatch = new Map<string, BloomieSignalDelivery[]>();
    for (const d of deliveries) {
      if (!d.news_batch_id) continue;
      const list = deliveriesByBatch.get(d.news_batch_id) || [];
      list.push(d);
      deliveriesByBatch.set(d.news_batch_id, list);
    }

    return {
      batches: batches.map((b) => ({
        ...b,
        articles: articlesByBatch.get(b.id) || [],
        deliveries: deliveriesByBatch.get(b.id) || [],
      })),
      processingItems,
    };
  } catch (error) {
    console.error('[fetchFlowData] Error:', error);
    return { batches: [], processingItems: [] };
  }
}

// ─── Bloomie v2: Ticker Demand, Source Quality, Feed ─────────────────────────

export async function fetchTickerDemand(): Promise<BloomieTickerDemand[]> {
  try {
    const response = await supabaseClient.get('/BloomieTickerDemand', {
      params: { order: 'demand_count.desc', is_active: 'eq.true', limit: 50 },
    });
    return (response.data || []) as BloomieTickerDemand[];
  } catch (error) {
    console.error('[fetchTickerDemand] Error:', error);
    return [];
  }
}

export async function fetchSourceBreakdown(days = 7): Promise<SourceBreakdown[]> {
  try {
    const since = new Date(Date.now() - days * 86400000).toISOString();
    const response = await supabaseClient.get('/BloomieNewsItems', {
      params: {
        select: 'source_name,relevance_score,source_tier',
        created_at: `gte.${since}`,
        limit: 1000,
      },
    });
    const items = (response.data || []) as { source_name: string; relevance_score: number; source_tier: number }[];

    // Aggregate by source
    const map = new Map<string, { count: number; totalRelevance: number; tier: number }>();
    for (const item of items) {
      const key = item.source_name || 'Unknown';
      const existing = map.get(key) || { count: 0, totalRelevance: 0, tier: item.source_tier || 40 };
      existing.count++;
      existing.totalRelevance += item.relevance_score || 0;
      map.set(key, existing);
    }

    return Array.from(map.entries())
      .map(([source_name, data]) => ({
        source_name,
        article_count: data.count,
        avg_relevance: Math.round(data.totalRelevance / data.count),
        tier: data.tier,
      }))
      .sort((a, b) => b.article_count - a.article_count);
  } catch (error) {
    console.error('[fetchSourceBreakdown] Error:', error);
    return [];
  }
}

export async function fetchTopStories(limit = 20): Promise<BloomieNewsBatch[]> {
  try {
    const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
    const response = await supabaseClient.get('/BloomieNewsBatches', {
      params: {
        order: 'relevance_score.desc',
        created_at: `gte.${since}`,
        limit,
      },
    });
    return (response.data || []) as BloomieNewsBatch[];
  } catch (error) {
    console.error('[fetchTopStories] Error:', error);
    return [];
  }
}

export async function fetchUserStockNews(watchlist: string[], limit = 20): Promise<BloomieNewsBatch[]> {
  if (!watchlist || watchlist.length === 0) return [];
  try {
    const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
    // Use overlaps filter to find batches with matching tickers
    const response = await supabaseClient.get('/BloomieNewsBatches', {
      params: {
        order: 'relevance_score.desc',
        created_at: `gte.${since}`,
        tickers: `ov.{${watchlist.join(',')}}`,
        limit,
      },
    });
    return (response.data || []) as BloomieNewsBatch[];
  } catch (error) {
    console.error('[fetchUserStockNews] Error:', error);
    return [];
  }
}

// ─── End Bloomie Agent ────────────────────────────────────────────────────────

export default supabaseClient;
